import { API } from "@/lib/api";
import { Divider, Frame, STYLES, Wordmark } from "@/components/MenuOrnaments";
import { MenuSheet } from "@/components/MenuSheet";

const A4 = { width: "210mm", height: "297mm" };

export function Crest({ text, color, size = 52 }) {
  const [a = "", b = ""] = (text || "").replace(/[^A-Za-zÀ-ú]/g, "").split("");
  return (
    <svg viewBox="0 0 120 140" style={{ width: `${size}mm`, height: `${size * 1.17}mm` }} data-testid="crest">
      <g fill="none" stroke={color} strokeWidth="1.4">
        <path d="M20 22 Q60 8 100 22 L100 70 Q100 110 60 130 Q20 110 20 70 Z" />
        <path d="M26 27 Q60 15 94 27 L94 70 Q94 104 60 122 Q26 104 26 70 Z" strokeWidth="0.7" />
        <path d="M20 22 Q12 14 18 6 Q26 2 30 12 M100 22 Q108 14 102 6 Q94 2 90 12 M60 9 Q54 0 60 -2 Q66 0 60 9" />
        <path d="M20 60 Q8 64 10 76 Q14 84 22 78 M100 60 Q112 64 110 76 Q106 84 98 78 M44 128 Q60 140 76 128" />
      </g>
      <text x="48" y="78" textAnchor="middle" fontFamily="'Pinyon Script', cursive" fontSize="46" fill={color}>{a}</text>
      <text x="72" y="100" textAnchor="middle" fontFamily="'Pinyon Script', cursive" fontSize="46" fill={color}>{b}</text>
    </svg>
  );
}

function Page({ stile, children, testId }) {
  const s = STYLES[stile];
  return (
    <div className="relative overflow-hidden" style={{ ...A4, background: s.bg, color: s.text, fontFamily: s.body }} data-testid={testId}>
      <Frame stile={stile} />
      <div className="absolute inset-x-[24mm] top-[24mm] bottom-[22mm] flex flex-col items-center justify-evenly text-center">{children}</div>
    </div>
  );
}

const caps = (s) => (s.caps ? { fontVariant: "small-caps", letterSpacing: "0.06em" } : {});
const Logo = ({ art, info, ok, h }) => {
  if (art.logo === "none") return null;
  if (ok) return <img src={`${API}/public/logo/${art.logo || info.logo}`} alt="logo" crossOrigin="anonymous" style={{ maxHeight: `${h}mm`, maxWidth: "140mm", objectFit: "contain", flexShrink: 0 }} />;
  return <Wordmark logo={art.logo || info.logo} color={STYLES[art.stile].title} w={h * 3.1} />;
};

function Cover({ art, info, ok }) {
  const s = STYLES[art.stile];
  return (
    <Page stile={art.stile} testId="booklet-cover">
      <Logo art={art} info={info} ok={ok} h={44} />
      <Divider stile={art.stile} size={6} />
      <div style={{ fontSize: "8mm", ...caps(s) }}>{art.occasione}</div>
      <div style={{ fontFamily: "'Pinyon Script', cursive", color: s.title, fontSize: "30mm", lineHeight: 1.05 }} data-testid="booklet-names">{art.nomi}</div>
      <Divider stile={art.stile} size={6} />
      <div style={{ fontSize: "10mm", letterSpacing: "0.2em", fontWeight: 600 }}>{art.data_txt}</div>
      {art.monogramma && <Crest text={art.monogramma} color={s.title} size={52} />}
    </Page>
  );
}

function Welcome({ art, info, ok }) {
  const s = STYLES[art.stile];
  return (
    <Page stile={art.stile} testId="booklet-welcome">
      <Logo art={art} info={info} ok={ok} h={44} />
      <Divider stile={art.stile} size={6} />
      <div style={{ fontFamily: s.head, color: s.text, fontSize: s.script ? "16mm" : "10mm", letterSpacing: "0.12em", fontWeight: 600 }}>{art.benvenuto_titolo}</div>
      <div style={{ fontSize: "7.5mm", lineHeight: 1.6, whiteSpace: "pre-line", fontWeight: 500, ...caps(s) }}>{art.benvenuto}</div>
      {art.augurio && <div style={{ fontSize: "9mm", letterSpacing: "0.15em", fontWeight: 600 }}>“{art.augurio}”</div>}
      <Divider stile={art.stile} size={6} />
      {art.monogramma && <Crest text={art.monogramma} color={s.title} size={50} />}
    </Page>
  );
}

function Thanks({ art, info, ok }) {
  const s = STYLES[art.stile];
  return (
    <Page stile={art.stile} testId="booklet-thanks">
      {art.monogramma && <Crest text={art.monogramma} color={s.title} size={58} />}
      <div style={{ fontSize: "7.2mm", lineHeight: 1.65, whiteSpace: "pre-line", fontWeight: 500, ...caps(s) }} data-testid="booklet-thanks-text">{art.ringraziamento}</div>
      {art.firma && <div style={{ fontFamily: "'Pinyon Script', cursive", fontSize: "17mm", alignSelf: "flex-end", color: s.text }}>{art.firma}</div>}
      <Logo art={art} info={info} ok={ok} h={32} />
    </Page>
  );
}

const half = (child) => (
  <div style={{ width: "148.5mm", height: "210mm", overflow: "hidden" }}>
    <div style={{ zoom: 0.7071 }}>{child}</div>
  </div>
);

export function Spread({ left, right, testId, last }) {
  return <div className="flex bg-white booklet-spread" style={{ width: "297mm", height: "210mm", overflow: "hidden", breakInside: "avoid", pageBreakAfter: last ? "auto" : "always" }} data-testid={testId}>{half(left)}<div style={{ width: 0, borderLeft: "0.2mm dashed #ccc" }} />{half(right)}</div>;
}

export function Booklet({ art, info, ok }) {
  return (
    <div className="space-y-6 print:space-y-0" data-testid="booklet">
      <div className="text-xs text-[#7B929C] text-center print:hidden">Foglio 1 • esterno (retro | copertina)</div>
      <Spread testId="booklet-outside" left={<Thanks art={art} info={info} ok={ok} />} right={<Cover art={art} info={info} ok={ok} />} />
      <div className="text-xs text-[#7B929C] text-center print:hidden">Foglio 2 • interno (benvenuto | menù)</div>
      <Spread last testId="booklet-inside" left={<Welcome art={art} info={info} ok={ok} />} right={<MenuSheet art={art} info={info} logoOk={false} noLogo />} />
    </div>
  );
}
